import EventRepeat from "@mui/icons-material/EventRepeat";
import { Button, Chip, Tooltip } from "@mui/material";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useProcessedManifest } from "hooks/useProcessedManifest";
import { useMemo, useState } from "react";
import { getDateLikeIsoDate } from "util/dates";

import { episodicDeclarationsEnabled } from "./episodicFlags";
import { ManifestForEpisodic } from "./markManifestEpisodic";
import { MarkManifestEpisodicDialog } from "./MarkManifestEpisodicDialog";

type EpisodicSummary = { exemptPounds: number; episodicEvents: { id: string; reason: string }[] };

function useManifestForEpisodic(
  processedManifestId: string | undefined,
  tenantId: string | undefined
): { manifest: ManifestForEpisodic | null; summary: EpisodicSummary | null; declarable: boolean } {
  const { processedManifest } = useProcessedManifest(processedManifestId, tenantId);
  const id = processedManifest?.id;
  const manifestTrackingNumber = processedManifest?.manifestTrackingNumber;
  const facilityId = processedManifest?.facility?.id;
  const shippedDate = getDateLikeIsoDate(String(processedManifest?.shippedDate ?? ""));
  const summary = processedManifest?.episodicSummary ?? null;
  const declarable = processedManifest?.episodicDeclarable ?? false;
  return useMemo(() => {
    if (!id || !manifestTrackingNumber || !facilityId || !shippedDate) {
      return { manifest: null, summary: null, declarable: false };
    }
    return {
      manifest: { processedManifestId: id, manifestTrackingNumber, facilityId, shippedDate },
      summary: summary && summary.episodicEvents.length > 0 ? summary : null,
      declarable,
    };
  }, [id, manifestTrackingNumber, facilityId, shippedDate, summary, declarable]);
}

/** The declared state: a chip that opens the pounds form; the form links to the event. */
function EpisodicDeclaredChip({
  summary,
  onClick,
}: {
  summary: EpisodicSummary;
  onClick: () => void;
}) {
  const reasons = summary.episodicEvents.map((event) => event.reason).join(", ");
  return (
    <Tooltip title={`Declared on ${reasons || "an episodic event"}. Edit the pounds.`}>
      <Chip size="small" color="info" variant="outlined" label="Episodic" onClick={onClick} />
    </Tooltip>
  );
}

export function MarkManifestEpisodicButton({
  processedManifestId,
  tenantIdOverride,
}: {
  processedManifestId: string | undefined;
  tenantIdOverride?: string;
}) {
  const { featureFlags } = useFeatureFlags();
  const { manifest, summary, declarable } = useManifestForEpisodic(
    processedManifestId,
    tenantIdOverride
  );
  const [open, setOpen] = useState(false);
  // A manifest with nothing status counts has nothing to declare; one already
  // declared keeps its chip so the stored pounds stay reachable.
  if (!episodicDeclarationsEnabled(featureFlags) || !manifest || (!summary && !declarable)) {
    return null;
  }
  return (
    <>
      {summary ? (
        <EpisodicDeclaredChip summary={summary} onClick={() => setOpen(true)} />
      ) : (
        <Button
          size="small"
          variant="outlined"
          startIcon={<EventRepeat />}
          onClick={() => setOpen(true)}
        >
          Mark as episodic
        </Button>
      )}
      <MarkManifestEpisodicDialog
        manifest={manifest}
        tenantIdOverride={tenantIdOverride}
        declaredEventId={summary?.episodicEvents[0]?.id}
        open={open}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
